import React, { useEffect, useRef, useState } from 'react';
import researchService from '../../services/researchService';

const EXT_PDF   = ['pdf'];
const EXT_IMAGE = ['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg'];
const EXT_TEXT  = ['txt', 'csv', 'json', 'xml', 'md', 'log'];

function getExt(filename = '') {
  return (filename.split('.').pop() || '').toLowerCase();
}

/**
 * Full-screen Bootstrap modal that previews a research file.
 *
 * - PDF  → embedded iframe (browser PDF viewer)
 * - Images → <img>
 * - Text/CSV/JSON → fetches raw text and renders in a scrollable <pre>
 * - Everything else → "preview not available, download instead"
 *
 * Props:
 *   file     – ResearchFile object (needs file_id, filename, content_type)
 *   onClose  – callback to close
 */
export default function FilePreviewModal({ file, onClose }) {
  const [url, setUrl] = useState(null);
  const [textContent, setTextContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const backdropRef = useRef(null);

  const ext = getExt(file?.filename);
  const isPdf   = EXT_PDF.includes(ext)   || (file?.content_type || '').includes('pdf');
  const isImage = EXT_IMAGE.includes(ext) || (file?.content_type || '').startsWith('image/');
  const isText  = EXT_TEXT.includes(ext)  || (file?.content_type || '').startsWith('text/');
  const canPreview = isPdf || isImage || isText;

  useEffect(() => {
    if (!file) return;
    let revoked = null;
    (async () => {
      setLoading(true);
      setError(null);
      setUrl(null);
      setTextContent(null);
      try {
        const { url: presignedUrl } = await researchService.getDownloadUrl(file.file_id);

        if (isText) {
          const resp = await fetch(presignedUrl);
          if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
          setTextContent(await resp.text());
        } else {
          setUrl(presignedUrl);
        }
      } catch (e) {
        setError(e.message || 'Failed to load file');
      } finally {
        setLoading(false);
      }
      return () => { if (revoked) URL.revokeObjectURL(revoked); };
    })();
  }, [file?.file_id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Close on backdrop click
  const handleBackdrop = (e) => {
    if (e.target === backdropRef.current) onClose();
  };

  // Close on Escape
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  if (!file) return null;

  return (
    <div
      ref={backdropRef}
      onClick={handleBackdrop}
      style={{
        position: 'fixed', inset: 0, zIndex: 1055,
        background: 'rgba(0,0,0,0.6)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
    >
      <div
        className="bg-white rounded shadow-lg d-flex flex-column"
        style={{ width: '90vw', maxWidth: 1100, height: '90vh' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="d-flex align-items-center justify-content-between px-3 py-2 border-bottom">
          <div>
            <span className="fw-bold">{file.title || file.filename}</span>
            {file.title && <span className="text-muted small ms-2">{file.filename}</span>}
          </div>
          <div className="d-flex gap-2">
            <button
              className="btn btn-sm btn-outline-secondary"
              onClick={async () => {
                try {
                  const { url: dl, filename } = await researchService.getDownloadUrl(file.file_id);
                  const a = document.createElement('a');
                  a.href = dl; a.download = filename || file.filename;
                  a.target = '_blank'; a.rel = 'noopener noreferrer';
                  document.body.appendChild(a); a.click(); document.body.removeChild(a);
                } catch { /* ignore */ }
              }}
            >
              <i className="bi bi-download me-1" />Download
            </button>
            <button className="btn btn-sm btn-outline-secondary" onClick={onClose}>
              <i className="bi bi-x-lg" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-grow-1 overflow-hidden position-relative">
          {loading && (
            <div className="d-flex align-items-center justify-content-center h-100">
              <div className="spinner-border text-primary" />
            </div>
          )}

          {!loading && error && (
            <div className="d-flex align-items-center justify-content-center h-100 text-danger">
              <i className="bi bi-exclamation-triangle me-2" />{error}
            </div>
          )}

          {!loading && !error && !canPreview && (
            <div className="d-flex flex-column align-items-center justify-content-center h-100 text-muted">
              <i className="bi bi-file-earmark fs-1 mb-3" />
              <div>Preview not available for this file type.</div>
            </div>
          )}

          {!loading && !error && canPreview && isPdf && url && (
            <iframe
              src={url}
              title={file.filename}
              style={{ width: '100%', height: '100%', border: 'none' }}
            />
          )}

          {!loading && !error && canPreview && isImage && url && (
            <div className="d-flex align-items-center justify-content-center h-100 p-3 bg-light">
              <img
                src={url}
                alt={file.filename}
                style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
              />
            </div>
          )}

          {!loading && !error && canPreview && isText && textContent !== null && (
            <pre
              style={{
                height: '100%', overflowY: 'auto', overflowX: 'auto',
                margin: 0, padding: '1rem', fontSize: '0.82rem',
                background: '#f8f9fa', whiteSpace: 'pre-wrap', wordBreak: 'break-word',
              }}
            >
              {textContent}
            </pre>
          )}
        </div>
      </div>
    </div>
  );
}
